import { useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { useGlobalStore } from "@/context/useGlobalStore";
import { ROUTE_QUERY_GC_TIME, ROUTE_QUERY_STALE_TIME } from "@/hooks/common/queryDefaults";

/**
 * Generic hook for fetching a single entity by ID with global store cache.
 *
 * @param {object} options
 * @param {string}   options.storeKey   - Key used in the global store to cache entity list
 * @param {string|number} options.entityId - The entity ID to fetch
 * @param {Array}    options.queryKey   - React Query cache key array
 * @param {Function} options.queryFn    - Async function receiving (entityId) → entity | null
 * @param {boolean}  [options.enabled]  - Override enabled flag (defaults to Boolean(entityId))
 */
export function useEntityById({ storeKey, entityId, queryKey, queryFn, enabled }) {
    const setStateSmart = useGlobalStore((s) => s.setStateSmart);

    const cached = useGlobalStore(
        useCallback(
            (state) =>
                (state[storeKey] || []).find(
                    (item) => String(item?.id) === String(entityId)
                ) || null,
            [storeKey, entityId]
        )
    );

    const isEnabled =
        enabled !== undefined
            ? enabled
            : Boolean(entityId) && String(entityId).trim() !== "";

    const { data, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: () => queryFn(entityId),
        staleTime: ROUTE_QUERY_STALE_TIME,
        gcTime: ROUTE_QUERY_GC_TIME,
        enabled: isEnabled,
        placeholderData: cached || undefined,
    });

    useEffect(() => {
        if (!data?.id) return;
        setStateSmart(storeKey, [data], "id");
    }, [setStateSmart, data, storeKey]);

    return { data: data ?? cached, isFetching, refetch };
}
